import {
  Audits,
  DetailsLoading,
  getResponseErrorMessage,
  useEvents,
  WhenWorkUpdated,
  workV4Query,
} from '@diana/core';
import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { IWorkAuditsProps } from './WorkAudits.types';
import { WORK_PAGE_NS } from '../../i18n';
import { WorkAudit } from './WorkAudit';
import { useDebouncedCallback } from 'use-debounce';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export const WorkAudits = (props: IWorkAuditsProps) => {
  const { workId, flow, isLoading: isWorkLoading } = props;
  const { t } = useTranslation(WORK_PAGE_NS);
  const [keyword, setKeyword] = useState<string>('');
  const query = workV4Query.getWorkAuditEvents({ workId: workId ?? '' });
  const {
    isFetching: isLoading,
    data: workAudits,
    error,
    refetch,
  } = useQuery({
    queryKey: query.key,
    queryFn: query.fn,
    enabled: Boolean(workId),
  });

  const errorMessage = useMemo(() => getResponseErrorMessage(error), [error]);
  const debouncedRefetch = useDebouncedCallback(refetch, 500);

  useEvents((ctx) => {
    WhenWorkUpdated.attach(ctx, (updatedWork) => {
      if (updatedWork.id === workId) {
        debouncedRefetch();
      }
    });
  }, []);

  const filteredWorkAudits = useMemo(() => {
    if (!workAudits) return [];
    const lcKeyword = keyword.toLocaleLowerCase();
    return workAudits.filter((audit) =>
      JSON.stringify(audit).toLocaleLowerCase().includes(lcKeyword),
    );
  }, [keyword, workAudits]);

  const Content = useMemo(() => {
    return filteredWorkAudits.map((audit, index) => (
      <Box key={index} pt={1}>
        <WorkAudit key={index} keyword={keyword} workId={workId} flow={flow} audit={audit} />
      </Box>
    ));
  }, [filteredWorkAudits, flow, keyword, workId]);

  if (isWorkLoading) {
    return <DetailsLoading />;
  }

  return (
    <Audits
      searchProps={{
        keyword,
        setKeyword,
      }}
      labels={{
        search: t('activity.labels.search'),
        noResult: t('activity.labels.noResult'),
      }}
      auditsExist={Boolean(filteredWorkAudits.length)}
      isLoading={isLoading}
      errorMessage={errorMessage}
    >
      {Content}
    </Audits>
  );
};
